@if (visible) {
  <div class="staff-access mt-3">
    <span class="access-toggle cursorPointer" role="button" tabindex="0" (click)="toggle()" (keydown.enter)="toggle()"
          data-testid="staff-access-toggle">
      <span class="fa-light fa-fw" [ngClass]="open ? 'fa-chevron-up' : 'fa-chevron-down'"></span>
      <strong>{{ 'STAFF_ACCESS_DETAILS' | translate }}</strong>
    </span>

    @if (open) {
      <div class="card card-borders mt-2 p-3">
        @if (loading) {
          <img class="fadeLoader centerLoader logoSmall" src="assets/img/loading_logo.svg"/>
        } @else if (loadError) {
          <div class="alert alert-danger mb-0">{{ 'STAFF_ACCESS_LOAD_FAILED' | translate }}</div>
        } @else if (access) {
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div>
              {{ 'ROLE' | translate }}: <strong data-testid="staff-access-role">{{ roleName }}</strong>
              @if (overrideCount > 0 && !editing) {
                <span class="badge badge-warning ml-2" data-testid="staff-access-override-count">
                  {{ 'STAFF_ACCESS_OVERRIDE_COUNT' | translate : { count: overrideCount } }}
                </span>
              }
            </div>
            @if (!editing && !changingRole) {
              <div>
                @if (canChangeRole) {
                  <button type="button" class="btn btn-outline-primary btn-sm" (click)="startRoleChange()" data-testid="change-role">
                    <span class="fa-light fa-fw fa-user-shield"></span> {{ 'STAFF_ROLE_CHANGE' | translate }}
                  </button>
                }
                @if (canEditOverrides) {
                  <button type="button" class="btn btn-outline-primary btn-sm ml-2" (click)="startEditing()" data-testid="edit-overrides">
                    <span class="fa-light fa-fw fa-sliders"></span> {{ 'STAFF_ACCESS_EDIT_INDIVIDUAL' | translate }}
                  </button>
                }
              </div>
            }
          </div>

          <!-- Role change: the same roles and limits as the staff edit form (role-choice.ts) -->
          @if (changingRole) {
            <div class="mb-3" data-testid="role-change">
              @if (rolesLoading) {
                <img class="fadeLoader logoSmall" src="assets/img/loading_logo.svg"/>
              } @else if (roleAboveOwnAccess) {
                <small class="text-muted d-block mb-2" data-testid="role-change-locked">{{ 'STAFF_ROLE_CANNOT_CHANGE' | translate }}</small>
                <button type="button" class="btn btn-outline-primary btn-sm" (click)="changingRole = false">
                  <span class="fa-light fa-fw fa-xmark"></span> {{ 'CANCEL' | translate }}
                </button>
              } @else {
                <mat-form-field class="w-100">
                  <mat-select [(ngModel)]="roleValue" data-testid="role-change-select">
                    <mat-optgroup [label]="'STAFF_ROLE_GROUP_BUILT_IN' | translate">
                      @for (option of builtInOptions; track option.level) {
                        <mat-option [value]="'builtIn:' + option.level" [disabled]="!canGrantBuiltIn(option.level)"
                                    [attr.data-testid]="'role-change-opt-' + option.level">
                          {{ option.nameKey | translate }}
                          @if (!canGrantBuiltIn(option.level)) {
                            <small class="text-muted"> · {{ 'STAFF_ROLE_ABOVE_OWN_ACCESS' | translate }}</small>
                          }
                        </mat-option>
                      }
                    </mat-optgroup>
                    @if (customOptions.length > 0) {
                      <mat-optgroup [label]="'STAFF_ROLE_GROUP_CUSTOM' | translate">
                        @for (role of customOptions; track role.id) {
                          <mat-option [value]="'custom:' + role.id" [disabled]="!canGrantCustom(role)"
                                      [attr.data-testid]="'role-change-custom-' + role.id">
                            <span class="fa-light fa-fw" [ngClass]="role.icon || 'fa-user'"></span> {{ role.name }}
                            @if (!canGrantCustom(role)) {
                              <small class="text-muted"> · {{ 'STAFF_ROLE_ABOVE_OWN_ACCESS' | translate }}</small>
                            }
                          </mat-option>
                        }
                      </mat-optgroup>
                    }
                  </mat-select>
                </mat-form-field>
                <button type="button" class="btn btn-primary btn-sm" (click)="saveRole()" [disabled]="saving" data-testid="save-role">
                  <span class="fa-light fa-fw fa-floppy-disk"></span> {{ 'SAVE' | translate }}
                </button>
                <button type="button" class="btn btn-outline-primary btn-sm ml-2" (click)="changingRole = false">
                  <span class="fa-light fa-fw fa-xmark"></span> {{ 'CANCEL' | translate }}
                </button>
              }
            </div>
          }

          @if (editing) {
            <p class="small text-muted">{{ 'STAFF_ACCESS_INDIVIDUAL_HELP' | translate }}</p>
          }

          @if (saveError) {
            <div class="alert alert-danger">{{ saveError }}</div>
          }

          <table class="table table-borderless table-sm access-table mb-0">
            <tbody>
              @for (area of areas; track area.key) {
                <tr [ngClass]="{ 'child-row': area.parentKey }">
                  <td [style.padding-left.rem]="0.5 + depth(area) * 1.25">{{ area.displayNameKey | translate }}</td>
                  @if (!editing) {
                    <td [ngClass]="{ 'text-muted': access.effective[area.key]?.level === 'None' }">
                      {{ levelLabel(access.effective[area.key]?.level ?? 'None') }}
                    </td>
                    <td class="small text-muted text-right">
                      @if (access.effective[area.key]?.source === 'Override') {
                        <span class="badge badge-warning">{{ sourceLabel(area) }}</span>
                      } @else {
                        {{ sourceLabel(area) }}
                      }
                    </td>
                  } @else {
                    <td colspan="2" class="text-right">
                      <mat-button-toggle-group [value]="overrides[area.key]" (change)="setOverride(area.key, $event.value)"
                                               hideSingleSelectionIndicator>
                        <mat-button-toggle value="Role" matTooltip="{{ levelLabel(access.effective[area.key]?.level ?? 'None') }}">
                          {{ 'STAFF_ACCESS_ROLE_DEFAULT' | translate }}
                        </mat-button-toggle>
                        <mat-button-toggle value="None">{{ 'ACCESS_NONE' | translate }}</mat-button-toggle>
                        <mat-button-toggle value="Read" [disabled]="aboveOwnAccess(area.key, 'Read')">{{ 'ACCESS_READ' | translate }}</mat-button-toggle>
                        <mat-button-toggle value="ReadWrite" [disabled]="aboveOwnAccess(area.key, 'ReadWrite')">{{ 'ACCESS_READWRITE' | translate }}</mat-button-toggle>
                      </mat-button-toggle-group>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>

          @if (editing) {
            <div class="mt-3">
              <button type="button" class="btn btn-primary" (click)="saveOverrides()" [disabled]="saving" data-testid="save-overrides">
                <span class="fa-light fa-fw fa-floppy-disk"></span> {{ 'SAVE' | translate }}
              </button>
              <button type="button" class="btn btn-outline-primary ml-2" (click)="editing = false">
                <span class="fa-light fa-fw fa-xmark"></span> {{ 'CANCEL' | translate }}
              </button>
            </div>
          }
        }
      </div>
    }
  </div>
}
